// components/Admin/UserAccessPage.jsx — User access management for admins
import React, { useState } from "react";
import {
  Avatar, Badge, Button, Dialog, DialogActions, DialogBody, DialogContent,
  DialogSurface, DialogTitle, DialogTrigger, Field, Input, Select, Spinner,
  Text, Tooltip, Switch, Textarea,
} from "@fluentui/react-components";
import {
  AddCircle24Regular, Delete24Regular, Edit24Regular, Person24Regular,
  ShieldLock24Regular, PeopleTeam24Regular, ArrowSync24Regular,
  LockClosed24Regular, Warning24Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import PeoplePicker from "../shared/PeoplePicker";

const ROLES = [
  { value: "admin",   label: "Admin",   desc: "Full access including user management and settings",          color: "#7B2D8B", bg: "#F3E8FF" },
  { value: "editor",  label: "Editor",  desc: "Activity Tracker admin (view all, bulk upload, GM report). Staff-level access everywhere else.", color: "#B8430A", bg: "#FFF0E6" },
  { value: "manager", label: "Manager", desc: "View all, enter data, manage entity schema, full AT access",  color: "#006E6E", bg: "#E0F7F7" },
  { value: "staff",   label: "Staff",   desc: "Data entry, dashboard, analytics and reports",                color: "#005A9E", bg: "#E3F0FF" },
  { value: "viewer",  label: "Viewer",  desc: "Read-only access to dashboard and reports",                   color: "#4A4A4A", bg: "#F0F0F0" },
];

const EMPTY_FORM = { email: "", name: "", role: "staff", department: "", notes: "", isActive: true };

function RoleBadge({ role }) {
  const r = ROLES.find((x) => x.value === role) || ROLES[3];
  return (
    <span style={{ background: r.bg, color: r.color, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
      {r.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, count, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #EDEBE9", borderRadius: 12, padding: "18px 22px", display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 140 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon style={{ color, fontSize: 22 }} />
      </div>
      <div>
        <Text style={{ fontSize: 26, fontWeight: 800, color: "#1A1A2E", display: "block", lineHeight: 1.1 }}>{count}</Text>
        <Text style={{ fontSize: 12, color: "#777", fontWeight: 500 }}>{label}</Text>
      </div>
    </div>
  );
}

// Read-only user card shown in the Edit dialog
function UserInfoCard({ name, email, department }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#F8F9FC", border: "1.5px solid #E8EAED", borderRadius: 10 }}>
      <Avatar name={name} size={40} color="brand" />
      <div>
        <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 14, display: "block" }}>{name}</Text>
        <Text style={{ fontSize: 12, color: "#0066FF", display: "block" }}>{email}</Text>
        {department && <Text style={{ fontSize: 12, color: "#888" }}>{department}</Text>}
      </div>
    </div>
  );
}

export default function UserAccessPage() {
  const { users, role, isAdmin, loading, isDemoMode, spError, addUser, editUser, removeUser, refresh, currentUserEmail } = useUserAccess();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pickedUser, setPickedUser] = useState(null); // O365 user picked from PeoplePicker (add mode only)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  if (!isAdmin) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 16 }}>
        <LockClosed24Regular style={{ fontSize: 48, color: "#D13438" }} />
        <Text size={500} weight="semibold" style={{ color: "#201F1E" }}>Access Restricted</Text>
        <Text style={{ color: "#605E5C", textAlign: "center", maxWidth: 380 }}>
          User Access Management is only available to Administrators. Your current role is <strong>{role}</strong>.
          Contact your SCM Administrator to request elevated access.
        </Text>
      </div>
    );
  }

  const activeUsers = users.filter((u) => u.isActive);
  const counts = ROLES.reduce((acc, r) => { acc[r.value] = activeUsers.filter((u) => u.role === r.value).length; return acc; }, {});

  function openAdd() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setPickedUser(null);
    setError("");
    setDialogOpen(true);
  }

  function openEdit(user) {
    setEditTarget(user);
    setPickedUser(null); // edit mode uses read-only card, not picker
    setForm({ email: user.email, name: user.name, role: user.role, department: user.department || "", notes: user.notes || "", isActive: user.isActive });
    setError("");
    setDialogOpen(true);
  }

  // When O365 user is picked in Add mode — auto-fill name, email, department
  function handlePickerChange(user) {
    setPickedUser(user);
    if (user) {
      setForm((p) => ({
        ...p,
        name: user.name || "",
        email: user.email || "",
        department: user.department || p.department,
      }));
    } else {
      setForm((p) => ({ ...p, name: "", email: "", department: "" }));
    }
  }

  async function handleSave() {
    if (!form.email.trim() || !form.name.trim()) {
      setError(editTarget ? "Name and email are required." : "Please select a user from Office 365 first.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError("Please enter a valid email address."); return; }
    setSaving(true);
    setError("");
    try {
      if (editTarget) {
        await editUser(editTarget.id, { ...form, email: form.email.trim(), name: form.name.trim() });
      } else {
        const duplicate = users.find((u) => u.email.toLowerCase() === form.email.trim().toLowerCase());
        if (duplicate) { setError("A user with this email already has access."); setSaving(false); return; }
        await addUser({ ...form, email: form.email.trim(), name: form.name.trim() });
      }
      setDialogOpen(false);
    } catch (e) {
      setError(e.message || "Failed to save user.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await removeUser(deleteTarget.id);
      setDeleteTarget(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <ShieldLock24Regular style={{ color: "#0066FF", fontSize: 28 }} />
            <Text size={600} weight="bold" style={{ color: "#001A3D" }}>User Access Management</Text>
          </div>
          <Text style={{ color: "#605E5C", fontSize: 14 }}>
            Control who can access the SCM Dashboard and manage their permission levels.
            {isDemoMode && <Badge appearance="tint" color="warning" style={{ marginLeft: 10, fontSize: 11 }}>Demo Mode — data saved locally</Badge>}
          </Text>
          {isDemoMode && spError && (
            <div style={{ marginTop: 10, background: "#FFF4EC", border: "1px solid #F7C59F", borderRadius: 8, padding: "10px 14px", display: "flex", gap: 8, alignItems: "flex-start" }}>
              <Warning24Regular style={{ color: "#E65C00", flexShrink: 0, fontSize: 18, marginTop: 1 }} />
              <div>
                <Text style={{ fontWeight: 700, color: "#E65C00", fontSize: 12, display: "block" }}>SharePoint connection failed: {spError}</Text>
                <Text style={{ fontSize: 12, color: "#8B4513" }}>Verify the <strong>SCMUserAccess</strong> list exists and column names match exactly.</Text>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button appearance="subtle" icon={<ArrowSync24Regular />} onClick={refresh} disabled={loading}>Refresh</Button>
          <Button appearance="primary" icon={<AddCircle24Regular />} onClick={openAdd} style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}>
            Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        <StatCard icon={PeopleTeam24Regular} label="Total Active" count={activeUsers.length} color="#0066FF" />
        <StatCard icon={ShieldLock24Regular} label="Admins" count={counts.admin || 0} color="#7B2D8B" />
        <StatCard icon={Person24Regular} label="Editors" count={counts.editor || 0} color="#B8430A" />
        <StatCard icon={Person24Regular} label="Managers" count={counts.manager || 0} color="#006E6E" />
        <StatCard icon={Person24Regular} label="Staff" count={counts.staff || 0} color="#005A9E" />
        <StatCard icon={Person24Regular} label="Viewers" count={counts.viewer || 0} color="#4A4A4A" />
      </div>

      {/* Roles Reference */}
      <div style={{ background: "#F8F9FF", border: "1px solid #E3EEFF", borderRadius: 10, padding: "16px 20px", marginBottom: 24 }}>
        <Text style={{ fontSize: 12, fontWeight: 700, color: "#0066FF", letterSpacing: "0.5px", display: "block", marginBottom: 10 }}>ROLE PERMISSIONS</Text>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {ROLES.map((r) => (
            <div key={r.value} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <RoleBadge role={r.value} />
              <Text style={{ fontSize: 12, color: "#605E5C" }}>{r.desc}</Text>
            </div>
          ))}
        </div>
      </div>

      {/* User Table */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner label="Loading users..." /></div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #EDEBE9", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#F8F9FF", borderBottom: "1px solid #EDEBE9" }}>
                  {["User", "Role", "Department", "Status", "Granted By", "Date Added", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#605E5C", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: "48px 24px", textAlign: "center", color: "#999" }}>No users found. Click "Add User" to get started.</td></tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} style={{ borderBottom: "1px solid #F3F2F1", opacity: user.isActive ? 1 : 0.55, transition: "background 0.1s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#F8F9FF"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar name={user.name} size={32} color={user.role === "admin" ? "brand" : "neutral"} />
                          <div>
                            <Text style={{ fontWeight: 600, color: "#1A1A2E", display: "block" }}>{user.name}</Text>
                            <Text style={{ fontSize: 12, color: "#888" }}>{user.email}</Text>
                            {user.email.toLowerCase() === currentUserEmail.toLowerCase() && (
                              <Badge appearance="tint" color="brand" style={{ fontSize: 10, marginTop: 2 }}>You</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px 16px" }}><RoleBadge role={user.role} /></td>
                      <td style={{ padding: "12px 16px", color: "#605E5C" }}>{user.department || "—"}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ background: user.isActive ? "#E9F5E9" : "#F0F0F0", color: user.isActive ? "#107C10" : "#888", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px", color: "#605E5C", fontSize: 13 }}>{user.grantedBy || "—"}</td>
                      <td style={{ padding: "12px 16px", color: "#605E5C", fontSize: 13, whiteSpace: "nowrap" }}>
                        {user.grantedAt ? new Date(user.grantedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <Tooltip content="Edit user" relationship="label">
                            <Button appearance="subtle" size="small" icon={<Edit24Regular />} onClick={() => openEdit(user)} />
                          </Tooltip>
                          <Tooltip content={user.email.toLowerCase() === currentUserEmail.toLowerCase() ? "Cannot delete yourself" : "Remove user"} relationship="label">
                            <Button appearance="subtle" size="small" icon={<Delete24Regular />}
                              style={{ color: "#D13438" }}
                              disabled={user.email.toLowerCase() === currentUserEmail.toLowerCase()}
                              onClick={() => setDeleteTarget(user)} />
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(_, d) => setDialogOpen(d.open)}>
        <DialogSurface style={{ maxWidth: 500, width: "95vw" }}>
          <DialogBody>
            <DialogTitle>{editTarget ? "Edit User Access" : "Add User from Office 365"}</DialogTitle>
            <DialogContent>
              <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>

                {editTarget ? (
                  /* Edit mode — show read-only user card, no picker */
                  <div>
                    <Text style={{ fontSize: 12, fontWeight: 700, color: "#888", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>USER</Text>
                    <UserInfoCard name={form.name} email={form.email} department={form.department} />
                  </div>
                ) : (
                  /* Add mode — O365 PeoplePicker */
                  <div>
                    <Text style={{ fontSize: 12, fontWeight: 700, color: "#0066FF", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>SEARCH OFFICE 365 DIRECTORY</Text>
                    <Text style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 10 }}>
                      Name, email and department are pulled automatically from your O365 tenant.
                    </Text>
                    <PeoplePicker
                      label="Find user"
                      required
                      value={pickedUser}
                      onChange={handlePickerChange}
                      placeholder="Search by name or email..."
                    />
                  </div>
                )}

                {/* Department — auto-filled from O365 but editable */}
                <Field label="Department">
                  <Input
                    value={form.department}
                    onChange={(_, d) => setForm((p) => ({ ...p, department: d.value }))}
                    placeholder={editTarget ? "e.g. Supply Chain" : "Auto-filled from O365, or type to override"}
                  />
                </Field>

                <Field label="Role" required>
                  <Select value={form.role} onChange={(_, d) => setForm((p) => ({ ...p, role: d.value }))}>
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
                    ))}
                  </Select>
                </Field>

                <Field label="Status">
                  <Switch
                    label={form.isActive ? "Active" : "Inactive"}
                    checked={form.isActive}
                    onChange={(_, d) => setForm((p) => ({ ...p, isActive: d.checked }))}
                  />
                </Field>

                <Field label="Notes">
                  <Textarea value={form.notes} onChange={(_, d) => setForm((p) => ({ ...p, notes: d.value }))} placeholder="Optional notes about this user's access..." rows={2} />
                </Field>

                {error && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FDE7E9", borderRadius: 8, color: "#D13438", fontSize: 13 }}>
                    <Warning24Regular style={{ flexShrink: 0 }} />{error}
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={saving}>Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={saving || (!editTarget && !pickedUser)}
                style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}
              >
                {saving ? <Spinner size="tiny" /> : editTarget ? "Save Changes" : "Add User"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null); }}>
        <DialogSurface style={{ maxWidth: 400 }}>
          <DialogBody>
            <DialogTitle>Remove User Access</DialogTitle>
            <DialogContent>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
                <Text>Remove access for <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?</Text>
                <Text style={{ color: "#605E5C", fontSize: 13 }}>They will no longer be able to access the SCM Dashboard. You can re-add them at any time.</Text>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={deleting}>Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleDelete} disabled={deleting} style={{ background: "#D13438" }}>
                {deleting ? <Spinner size="tiny" /> : "Remove Access"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
